import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import {
  IVideoControllerComponentsCommonProps,
  IVideoElementControllerProps,
} from 'components/videoElementController/IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import { TMediaStream, IPlayerState } from 'types/common';
import { bind } from 'utils/bind';

export class StreamController {
  private _state: StateManager<IPlayerState>;
  private _props: IVideoControllerComponentsCommonProps;
  private _streamResetDelayId: ReturnType<typeof setTimeout>;
  private _config: IVideoElementControllerProps = {};
  private _logger: TLogger;

  private _eventsMap = {
    [EVideoElementControllerBusEvent.PLAYING]: this._cancelResetStream,
    [EVideoElementControllerBusEvent.LOADING]: this._cancelResetStream,
    [EVideoElementControllerBusEvent.LOADED]: this._cancelResetStream,
    [EVideoElementControllerBusEvent.PAUSED]: this._cancelResetStream,
    [EVideoElementControllerBusEvent.UPDATED]: this._cancelResetStream,
    [EVideoElementControllerBusEvent.CANCEL_RESET_STREAM]: this._cancelResetStream,
    [EVideoElementControllerBusEvent.STREAM_RESET]: this._resetStream,
    [EVideoElementControllerBusEvent.UPDATE_MEDIA_STREAM]: this.updateMediaStream,
    [EVideoElementControllerBusEvent.DESTROY]: this.destroy,
  };
  private _eventBus: TVideoControllerEventBus;

  constructor(props: IVideoControllerComponentsCommonProps) {
    this._state = props.state;
    this._eventBus = props.eventBus;
    this._logger = Logger(this._eventBus, 'VideoElementController - StreamController');
    this._props = props;

    this._logger.info('Created');
    this._logger.verbose('Initial state', props.state);

    this.init();
  }

  public setConfig(config: IVideoElementControllerProps) {
    this._logger.debug('Config update received, the new one:', config);
    this._config = config;
  }

  @bind
  public updateMediaStream(stream: TMediaStream) {
    this._logger.info('Updating media stream', { stream });
    const videoElement = this._props.getVideoElement();

    if (!videoElement) {
      this._logger.debug('No video element known. Skipping media stream update');
      return;
    }

    this._attachStream(stream);

    this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_START, {});
  }

  @bind
  public removeMediaStream() {
    this._logger.info('Removing media stream');
    const videoElement = this._props.getVideoElement();

    if (!videoElement) {
      this._logger.debug('No video element known. Skipping media stream remove');
      return;
    }

    this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_PAUSE, {});
    this._eventBus.publish(EVideoElementControllerBusEvent.REMOVE_MEDIA_STREAM, {});

    if (this._getIsSrcObjectAvailable()) {
      this._logger.debug('Setting srcObject to null');
      videoElement.srcObject = null;
    } else {
      this._logger.debug('Revoking object url', { url: videoElement.src });
      window.URL.revokeObjectURL(videoElement.src);
    }

    if (videoElement.src) {
      this._logger.debug('Removing src attribute from video');
      videoElement.removeAttribute('src');
    }
  }

  @bind
  public destroy() {
    this._logger.info('Destroying');
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });
  }

  @bind
  public init() {
    this._logger.info('Initialization requested, attaching eventBus listeners');
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  private _getIsSrcObjectAvailable = (): boolean =>
    this._props.getVideoElement()?.srcObject !== undefined;

  @bind
  private _attachStream(stream: TMediaStream) {
    this._logger.info('Attaching media stream', { stream });
    const videoElement = this._props.getVideoElement();
    if (!videoElement || !stream) {
      this._logger.debug('No video element or stream defined. Skipping stream set');
      return;
    }

    if (videoElement.src === stream) {
      this._logger.debug(
        'Current video element src attribute value is equal to the new stream. Skipping stream set',
      );
      return;
    }

    if (typeof stream === 'string') {
      this._logger.debug('Setting video element src attribute');
      videoElement.src = stream;
    } else if (this._getIsSrcObjectAvailable()) {
      this._logger.debug('Setting video element srcObject attribute');
      videoElement.srcObject = stream;
    } else {
      videoElement.src = window.URL.createObjectURL(stream as MediaSource);
      this._logger.debug(
        `Got MediaStream as stream, but srcObject is not available. Created new object URL: ${videoElement.src}`,
      );
    }
  }

  @bind
  private _cancelResetStream() {
    this._logger.debug('Stream reset action cancel required');
    if (this._streamResetDelayId) {
      clearTimeout(this._streamResetDelayId);
      this._logger.debug('Stream reset action cancelled');
    }
  }

  @bind
  private _resetStream({ timeout }: { reason?: string; timeout?: number }) {
    this._logger.debug('Reset stream required');
    if (!timeout) {
      this._logger.verbose('No timeout set, skipping stream reset', { timeout });
      return;
    }

    this._cancelResetStream();

    this._streamResetDelayId = setTimeout(() => {
      this._logger.debug('Resetting stream timeout fired');
      let hasStreamReset = false;
      if (this._config.onResetStream) {
        hasStreamReset = this._config.onResetStream();
      }

      const stream = this._state.getState().stream;

      if (stream && !hasStreamReset) {
        if (typeof stream === 'string' && stream.startsWith('blob')) {
          this._logger.debug('Streams seems to be object url. Skipping reset');
          return;
        }
        this._logger.debug('Resetting stream');
        this.removeMediaStream();
        this.updateMediaStream(stream);
      }
    }, timeout);
  }
}
